import type { MutableRefObject } from 'react';
import { useRef } from 'react';
import getSourceDimensions from '../get-source-dimensions';
import type { Resolution, VideoRef } from '../types';
import { calculateFaceAngleFromMatrix, isFaceBlurry, isFaceCloseEnough, isFaceInTheFrame, isFaceTooClose, isHeadStraight } from './face-utils';
import { useFaceModel } from './use-face-model-loader';
enum FaceStatus {
  OK = 'ok',
  faceTooFar = 'face-too-far',
  faceTooClose = 'face-too-close',
  faceOutsideTheFrame = 'face-outside-the-frame',
  headNotStraight = 'head-not-straight',
  headMoving = 'head-moving',
  blurry = 'blurry',
  sunglasses = 'sunglasses',
  detecting = 'detecting',
}

// Movement greater than this fraction of face width between frames is considered
// "too much movement". At 200ms detection interval this catches head shaking
// while tolerating normal micro-movements (breathing, minor adjustment).
const MOVEMENT_THRESHOLD_RATIO = 0.12;
const useFaceDetection = () => {
  const landmarker = useFaceModel();
  const prevFaceCenterRef = useRef<{
    x: number;
    y: number;
  } | null>(null);
  const getFaceStatus = async (canvasRef: MutableRefObject<HTMLCanvasElement | undefined>, videoRef: VideoRef, options: {
    width: number;
    height: number;
    frameDimensions: {
      width: number;
      height: number;
      frameOffsetX: number;
      frameOffsetY: number;
    };
    videoResolution: Resolution | undefined;
  }) => {
    const {
      width,
      height,
      frameDimensions,
      videoResolution
    } = options;
    if (!landmarker) {
      console.warn('Face model not loaded - returning');
      return FaceStatus.detecting;
    }
    try {
      if (!videoRef.current) {
        console.warn('Video element is missing');
        return FaceStatus.detecting;
      }
      if (!videoResolution?.width || !videoResolution.height) {
        console.warn('Video resolution is missing');
        return FaceStatus.detecting;
      }
      const faceDetectionCanvas = canvasRef.current;
      if (!faceDetectionCanvas) {
        console.warn('Face detection canvas not found');
        return FaceStatus.detecting;
      }
      faceDetectionCanvas.width = width;
      faceDetectionCanvas.height = height;
      const faceDetectionCtx = canvasRef.current?.getContext('2d', {
        willReadFrequently: true
      });
      if (!faceDetectionCtx) {
        console.warn('Face detection context not found');
        return FaceStatus.detecting;
      }
      const {
        sx,
        sy,
        sWidth,
        sHeight
      } = getSourceDimensions({
        videoRef,
        videoResolution,
        desiredImageWidth: width,
        desiredImageHeight: height,
        centerOffsetX: 0,
        centerOffsetY: 0
      });
      faceDetectionCtx.clearRect(0, 0, width, height);
      faceDetectionCtx.drawImage(videoRef.current, sx, sy, sWidth, sHeight, 0, 0, width, height);

      // MediaPipe detect is synchronous
      const result = landmarker.detect(faceDetectionCanvas);
      if (!result.faceLandmarks || result.faceLandmarks.length === 0) {
        prevFaceCenterRef.current = null;
        return FaceStatus.detecting;
      }

      // Derive bounding box from normalized landmarks (0–1 range → canvas pixels)
      const landmarks = result.faceLandmarks[0];
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;
      for (const lm of landmarks) {
        if (lm.x < minX) minX = lm.x;
        if (lm.x > maxX) maxX = lm.x;
        if (lm.y < minY) minY = lm.y;
        if (lm.y > maxY) maxY = lm.y;
      }
      const faceX = minX * width;
      const faceY = minY * height;
      const faceWidth = (maxX - minX) * width;
      const faceHeight = (maxY - minY) * height;
      const {
        width: frameWidth,
        height: frameHeight,
        frameOffsetX,
        frameOffsetY
      } = frameDimensions;

      // Order matters — check most disqualifying conditions first

      // 1. Face must be close enough
      if (!isFaceCloseEnough({
        frameWidth,
        frameHeight,
        faceWidth,
        faceHeight
      })) {
        prevFaceCenterRef.current = null;
        return FaceStatus.faceTooFar;
      }

      // 2. Face must not be too close (overfills the frame)
      if (isFaceTooClose({
        frameWidth,
        frameHeight,
        faceWidth,
        faceHeight
      })) {
        prevFaceCenterRef.current = null;
        return FaceStatus.faceTooClose;
      }

      // 3. Face must be inside the frame
      if (!isFaceInTheFrame({
        videoWidth: width,
        videoHeight: height,
        frameWidth,
        frameHeight,
        faceWidth,
        faceHeight,
        faceX,
        faceY,
        frameOffsetX,
        frameOffsetY
      })) {
        prevFaceCenterRef.current = null;
        return FaceStatus.faceOutsideTheFrame;
      }

      // 4. Face must not be moving too much between frames
      const centerX = faceX + faceWidth / 2;
      const centerY = faceY + faceHeight / 2;
      const movementThreshold = faceWidth * MOVEMENT_THRESHOLD_RATIO;
      if (prevFaceCenterRef.current) {
        const dx = centerX - prevFaceCenterRef.current.x;
        const dy = centerY - prevFaceCenterRef.current.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance > movementThreshold) {
          prevFaceCenterRef.current = {
            x: centerX,
            y: centerY
          };
          return FaceStatus.headMoving;
        }
      }
      prevFaceCenterRef.current = {
        x: centerX,
        y: centerY
      };

      // 5. Face must not be blurry (only catches heavy motion blur; transient blur is
      //    tolerated by the REQUIRED_SUCCESSES debounce in the autocapture hook)
      if (isFaceBlurry(faceDetectionCanvas, {
        x: faceX,
        y: faceY,
        width: faceWidth,
        height: faceHeight
      })) {
        return FaceStatus.blurry;
      }

      // 6. Head must be straight (re-enabled — uses MediaPipe transformation matrix)
      if (result.facialTransformationMatrixes && result.facialTransformationMatrixes.length > 0) {
        const angle = calculateFaceAngleFromMatrix(result.facialTransformationMatrixes[0].data);
        if (!isHeadStraight(angle)) {
          return FaceStatus.headNotStraight;
        }
      }
      return FaceStatus.OK;
    } catch (error) {
      console.warn(`Face detection failed with error: ${error}`);
      return FaceStatus.detecting;
    }
  };
  return {
    getFaceStatus
  };
};
export { FaceStatus };
export default useFaceDetection;